import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'
import { ArrowUpRight, Menu, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Container } from '@/components/generals/container'
import { navLinks } from '@/constants/portfolio'
import { siteConfig } from '@/constants/site'

const OBSERVED_SECTIONS = ['hero', ...navLinks.map(link => link.href.split('#')[1]).filter(Boolean)]

export const Header: FC = (): ReactNode => {
    const router = useRouter()
    const [scrolled, setScrolled] = useState(false)
    const [open, setOpen] = useState(false)
    const [active, setActive] = useState('')

    const isHome = router.pathname === '/'
    const activeId = isHome ? active : ''

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24)

        onScroll()
        window.addEventListener('scroll', onScroll, { passive: true })

        return () => window.removeEventListener('scroll', onScroll)
    }, [])

    // While the mobile menu is open: lock page scroll and close on Escape.
    useEffect(() => {
        if (!open) return

        const root = document.documentElement
        const previous = root.style.overflow
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') setOpen(false)
        }

        root.style.overflow = 'hidden'
        window.addEventListener('keydown', onKeyDown)

        return () => {
            root.style.overflow = previous
            window.removeEventListener('keydown', onKeyDown)
        }
    }, [open])

    useEffect(() => {
        const close = () => setOpen(false)

        router.events.on('routeChangeStart', close)
        router.events.on('hashChangeStart', close)

        return () => {
            router.events.off('routeChangeStart', close)
            router.events.off('hashChangeStart', close)
        }
    }, [router.events])

    // Highlight the nav link of the section currently in the middle of the screen.
    useEffect(() => {
        if (!isHome) return

        const sections = OBSERVED_SECTIONS
            .map(id => document.getElementById(id))
            .filter((element): element is HTMLElement => element !== null)

        const observer = new IntersectionObserver(entries => {
            entries.forEach(entry => {
                if (entry.isIntersecting) setActive(entry.target.id)
            })
        }, { rootMargin: '-45% 0px -50% 0px' })

        sections.forEach(section => observer.observe(section))

        return () => observer.disconnect()
    }, [isHome])

    return (
        <header className='fixed inset-x-0 top-0 z-50'>
            <div
                className={cn(
                    'relative z-10 border-b transition-[padding,background-color,border-color] duration-500',
                    scrolled && !open
                        ? 'border-white/[0.06] bg-background/75 py-3 backdrop-blur-xl'
                        : 'border-transparent py-5 md:py-6'
                )}
            >
                <Container className='flex items-center justify-between gap-6'>
                    <Link
                        href='/'
                        aria-label={`${siteConfig.name}, back to the home page`}
                        className='text-sm font-bold tracking-[0.32em] transition-opacity hover:opacity-80'
                    >
                        {siteConfig.shortName}
                        <sup className='ml-0.5 text-[0.6em] tracking-normal'>®</sup>
                    </Link>

                    <nav aria-label="Main" className='hidden md:block'>
                        <ul className='flex items-center gap-1'>
                            {navLinks.map(link => {
                                const isActive = activeId !== '' && link.href === `/#${activeId}`

                                return (
                                    <li key={link.href}>
                                        <Link
                                            href={link.href}
                                            aria-current={isActive ? 'true' : undefined}
                                            className={cn(
                                                'relative block rounded-full px-4 py-2 text-sm transition-colors',
                                                isActive ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'
                                            )}
                                        >
                                            {link.label}

                                            <span
                                                aria-hidden='true'
                                                className={cn(
                                                    'absolute bottom-0.5 left-1/2 size-1 -translate-x-1/2 rounded-full bg-brand-bright transition-opacity duration-300',
                                                    isActive ? 'opacity-100' : 'opacity-0'
                                                )}
                                            />
                                        </Link>
                                    </li>
                                )
                            })}
                        </ul>
                    </nav>

                    <div className='flex items-center gap-4'>
                        <span className='hidden text-sm text-muted-foreground xl:inline'>
                            Portfolio ©{siteConfig.year}
                        </span>

                        <Link
                            href='/#contact'
                            className='hidden h-10 items-center gap-1.5 rounded-full bg-primary pl-5 pr-4 text-sm font-medium text-primary-foreground transition hover:brightness-110 md:inline-flex'
                        >
                            Let's talk
                            <ArrowUpRight aria-hidden='true' className='size-4' />
                        </Link>

                        <button
                            type='button'
                            onClick={() => setOpen(value => !value)}
                            aria-expanded={open}
                            aria-controls='mobile-menu'
                            aria-label={open ? "Close menu" : "Open menu"}
                            className='grid size-11 place-items-center rounded-full border border-border bg-background/60 backdrop-blur md:hidden'
                        >
                            {open ? <X className='size-5' /> : <Menu className='size-5' />}
                        </button>
                    </div>
                </Container>
            </div>

            {open && (
                <div
                    id='mobile-menu'
                    className='fixed inset-0 flex flex-col bg-background pt-24 animate-in fade-in duration-300 md:hidden'
                >
                    <nav aria-label="Mobile" className='flex-1 overflow-y-auto'>
                        <Container>
                            <ul className='divide-y divide-border border-y border-border'>
                                {navLinks.map((link, index) => (
                                    <li
                                        key={link.href}
                                        className='animate-in fade-in slide-in-from-bottom-4 fill-mode-both duration-500'
                                        style={{ animationDelay: `${index * 60}ms` }}
                                    >
                                        <Link
                                            href={link.href}
                                            onClick={() => setOpen(false)}
                                            className='flex items-baseline justify-between py-5 text-4xl font-semibold tracking-tight'
                                        >
                                            {link.label}

                                            <span className='font-mono text-xs text-brand-bright'>
                                                0{index + 1}
                                            </span>
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </Container>
                    </nav>

                    <Container className='flex items-center justify-between gap-4 py-8'>
                        <p className='flex items-center gap-2 text-sm text-muted-foreground'>
                            <span aria-hidden='true' className='pulse-dot size-2 rounded-full bg-emerald-400' />
                            {siteConfig.availability}
                        </p>

                        <span className='text-sm text-muted-foreground'>
                            ©{siteConfig.year}
                        </span>
                    </Container>
                </div>
            )}
        </header>
    )
}
